import type { WidgetParams } from '@common/types/content.type';

import { ChangeDetectionStrategy, Component, inject, Input, type OnChanges, type SimpleChanges } from '@angular/core';
import { combineLatest, type Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { type WebLegacyWidgetEntity } from '@web/types';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';

import { ColumnsFacade } from '@web/store/page-structure/columns/columns.facade';
import { SectionsFacade } from '@web/store/page-structure/sections/sections.facade';
import { PagesFacade } from '@web/store/website/pages/pages.facade';
import { NgIf, AsyncPipe } from '@angular/common';
import { SettingsBodyComponent } from '../settings-body/settings-body.component';
import { ControlResolverComponent } from '@web/layout/content/page-content/resolvers/control-resolver.component';
import { widgetNameOf, widgetParentIdOf } from '@common/helpers/widget-envelope';

@Component({
    selector: 'web-widget-settings',
    templateUrl: './widget-settings.component.html',
    styleUrls: ['./widget-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf, SettingsBodyComponent, ControlResolverComponent, AsyncPipe],
})
export class WidgetSettingsComponent implements OnChanges {
    @Input() public widget!: WebLegacyWidgetEntity;

    private readonly widgetsService: WebWidgetsService = inject(WebWidgetsService);
    private readonly columnFacade: ColumnsFacade = inject(ColumnsFacade);
    private readonly sectionsFacade: SectionsFacade = inject(SectionsFacade);
    private readonly pagesFacade: PagesFacade = inject(PagesFacade);

    public readonly isEduSite$: Observable<boolean> = this.pagesFacade.isEduSite$;

    public extendedWidgetParams!: WidgetParams;

    public ngOnChanges(changes: SimpleChanges): void {
        const { widget } = changes;

        if (!widget || widget.previousValue?.id === widget.currentValue?.id) {
            return;
        }

        this.extendedWidgetParams = this.widgetsService.getExtendedWidgetParams(widgetNameOf(widget.currentValue));
    }

    public get isSpecificWidget$(): Observable<boolean> {
        if (!this.widget) {
            return of(false);
        }

        return combineLatest([this.columnFacade.columnsEntities$, this.sectionsFacade.sectionsEntities$]).pipe(
            map(([columns, sections]) => {
                const column = columns[widgetParentIdOf(this.widget)];
                if (!column) {
                    return false;
                }

                const section = sections[column.parentId];
                return section?.entitySubType === 'specific';
            })
        );
    }
}
